import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

export const nb = (n) => (n ?? 0).toLocaleString("nb-NO");
export const kr = (n) => `${Math.round(n || 0).toLocaleString("nb-NO")} kr`;
export const pct = (n) => (n == null ? "–" : `${n.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} %`);
export const COLOURS = ["hsl(var(--sol))", "hsl(var(--foreground))", "hsl(var(--room-4))", "hsl(var(--muted-foreground))", "hsl(var(--brand))"];

/** Change against the previous period – green up, quiet down, dash when there is nothing to compare with. */
export const Delta = ({ cur, prev, className, testId, suffix = "" }) => {
  if (!prev && !cur) return <span className={cn("inline-flex items-center gap-1 text-[12px] text-muted-foreground", className)} data-testid={testId}><Minus className="h-3 w-3" /> ingen data</span>;
  if (!prev) return <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-px text-[11.5px] font-medium tabular bg-[hsl(var(--status-confirmed-bg))] text-status-confirmed", className)} data-testid={testId}>Ny{suffix}</span>;
  const d = Math.round(((cur - prev) / prev) * 100);
  const up = d > 0, flat = d === 0;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-px text-[11.5px] font-medium tabular", up ? "bg-[hsl(var(--status-confirmed-bg))] text-status-confirmed" : flat ? "bg-secondary text-muted-foreground" : "bg-secondary text-foreground/70", className)} data-testid={testId} title={`Førre periode: ${nb(prev)}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : flat ? <Minus className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{up ? "+" : ""}{d} %{suffix}
    </span>
  );
};

/** Label · value with a thin proportional bar beneath – the quiet workhorse of every ranking list. */
export const BarRow = ({ label, sub, value, max, display, colour = "hsl(var(--sol))", testId, to }) => {
  const w = max ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const Tag = to ? "a" : "div";
  return (
    <Tag href={to} target={to ? "_blank" : undefined} rel={to ? "noopener noreferrer" : undefined} className={cn("block py-2", to && "group")} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-4 text-[13.5px]">
        <span className={cn("min-w-0 truncate", to && "group-hover:underline group-hover:underline-offset-4")}>{label}{sub && <span className="ml-2 text-[12px] text-muted-foreground">{sub}</span>}</span>
        <span className="shrink-0 font-medium tabular">{display ?? nb(value)}</span>
      </div>
      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-foreground/[0.06]"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${w}%`, background: colour }} /></div>
    </Tag>
  );
};

/** Big number with eyebrow – for the secondary metric strips inside panels. */
export const Figure = ({ label, value, hint, className, testId }) => (
  <div className={cn("min-w-0", className)} data-testid={testId}>
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    <p className="mt-1 truncate font-display text-[22px] font-semibold leading-none tracking-[-0.02em] tabular" data-testid={testId ? `${testId}-value` : undefined}>{value}</p>
    {hint && <p className="mt-1 truncate text-[12px] text-muted-foreground">{hint}</p>}
  </div>
);

export const ChartTip = ({ active, payload, label, rows }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-popover px-3 py-2 text-[12.5px] shadow-lift">
      <p className="t-mono-sm mb-1 text-muted-foreground">{label}</p>
      {payload.map((p) => { const r = rows?.find((x) => x.key === p.dataKey); return <p key={p.dataKey} className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: p.color || p.stroke || p.fill }} />{r?.label || p.dataKey}: <span className="font-medium tabular">{r?.fmt ? r.fmt(p.value) : nb(p.value)}</span></p>; })}
    </div>
  );
};

export const Empty = ({ text, className }) => <p className={cn("py-6 text-center text-[13px] text-muted-foreground", className)}>{text}</p>;
